"use client";

import { Calendar, Mail } from "lucide-react";
import { aboutMe } from "#/data/about";
import { Button } from "#/components/ui/button";
import { Card, CardContent } from "#/components/ui/card";
import { MagicCard } from "#/components/magicui/magic-card";
import { Badge } from "#/components/ui/badge";
import * as m from "motion/react-m";
import { useTheme } from "#/components/theme-provider";
import Cal from "@calcom/embed-react";
import { FaLinkedinIn } from "react-icons/fa";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "#/components/ui/drawer";

import dynamic from "next/dynamic";

const Section = dynamic(() => import("./section"), {
  ssr: false,
});

export const ContactSection = () => {
  const { theme } = useTheme();
  const calTheme = theme === "system" ? "auto" : theme;

  return (
    <Section id="contact" title="Contactez-moi">
      <div className="mb-8 flex flex-col items-center justify-center">
        <m.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <Badge className="bg-emerald-500 px-3 py-1 text-sm font-medium text-white hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700">
            {aboutMe.availability}
          </Badge>
        </m.div>
        <m.h3
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-2 text-2xl font-bold text-neutral-900 dark:text-neutral-100"
        >
          Échangeons sur vos projets
        </m.h3>
        <m.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mx-auto max-w-2xl text-neutral-600 dark:text-neutral-300"
        >
          Choisissez votre méthode de contact préférée pour discuter de votre projet.
        </m.p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="border-none p-0 shadow-none">
            <MagicCard gradientColor={"#0077B520"} className="h-full">
              <CardContent className="p-0">
                <a
                  href={aboutMe.socialLinks.linkedin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col justify-between p-6 md:p-8"
                >
                  <div>
                    <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#0077B5]/10 text-[#0077B5] dark:bg-[#0077B5]/20">
                      <FaLinkedinIn className="h-8 w-8" />
                    </div>
                    <h3 className="mb-3 text-2xl font-bold text-neutral-900 dark:text-neutral-100">LinkedIn</h3>
                    <p className="mb-6 max-w-md text-neutral-600 dark:text-neutral-300">
                      Pour une réponse rapide ou discuter de vos projets professionnels, contactez-moi directement sur
                      LinkedIn.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-2 font-medium text-[#0077B5]">
                    <span>Me contacter</span>
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </div>
                </a>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="border-none p-0 shadow-none">
            <MagicCard gradientColor={"#00d49220"} className="h-full">
              <CardContent className="p-0">
                <Drawer>
                  <DrawerTrigger asChild>
                    <button className="group flex h-full w-full cursor-pointer flex-col justify-between p-6 text-left md:p-8">
                      <div>
                        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20">
                          <Calendar className="h-8 w-8" />
                        </div>
                        <h3 className="mb-3 text-2xl font-bold text-neutral-900 dark:text-neutral-100">Calendrier</h3>
                        <p className="mb-6 max-w-md text-neutral-600 dark:text-neutral-300">
                          Réservez directement un créneau dans mon agenda pour un appel ou une visioconférence.
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-2 font-medium text-emerald-500">
                        <span>Prendre rendez-vous</span>
                        <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </div>
                    </button>
                  </DrawerTrigger>
                  <DrawerContent className="h-[85vh] overflow-hidden">
                    <DrawerHeader>
                      <DrawerTitle>Prendre rendez-vous</DrawerTitle>
                      <DrawerDescription>
                        Sélectionnez une date et un créneau horaire pour notre entretien.
                      </DrawerDescription>
                    </DrawerHeader>
                    <div className="flex-1 overflow-y-auto px-4 py-2">
                      <Cal calLink="codebyjeff" config={{ theme: calTheme }}></Cal>
                    </div>
                    <DrawerFooter>
                      <DrawerClose asChild>
                        <Button variant="outline">Fermer</Button>
                      </DrawerClose>
                    </DrawerFooter>
                  </DrawerContent>
                </Drawer>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className="border-none p-0 shadow-none">
            <MagicCard gradientColor={"#6b728020"} className="h-full">
              <CardContent className="flex h-full flex-col justify-between p-6 md:p-8">
                <div>
                  <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-neutral-500/10 text-neutral-500 dark:bg-neutral-500/20">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-neutral-900 dark:text-neutral-100">Email & Réseaux</h3>
                  <div className="mb-4">
                    <a
                      href={`mailto:${aboutMe.email}`}
                      className="inline-flex items-center gap-2 text-neutral-700 transition-colors hover:text-emerald-500 dark:text-neutral-300"
                    >
                      {aboutMe.email}
                    </a>
                  </div>
                </div>
                <div>
                  <p className="mb-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Retrouvez-moi sur</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(aboutMe.socialLinks).map(([platform, social]) => (
                      <m.a
                        key={platform}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ y: -3 }}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/80 text-neutral-700 transition-all hover:shadow-md dark:bg-neutral-800/80 dark:text-neutral-300"
                        aria-label={`Visiter mon profil ${platform}`}
                      >
                        <span className="block h-5 w-5">{social.icon}</span>
                      </m.a>
                    ))}
                  </div>
                </div>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>
      </div>
    </Section>
  );
};
